(() => {
  const mobileQuery = window.matchMedia('(max-width: 1023px)');
  const desktopQuery = window.matchMedia('(min-width: 1024px)');

  const initCatalog = (root) => {
    if (!root || root.dataset.catalogReady === 'true') return;

    const menu = root.querySelector('.lcg-shop-menu');
    const menuSummary = menu?.querySelector(':scope > summary');
    const catalog = menu?.querySelector('[data-lcg-catalog]');
    if (!menu || !menuSummary || !catalog) return;

    root.dataset.catalogReady = 'true';

    const universeButtons = [...catalog.querySelectorAll('[data-lcg-universe]')];
    const universePanels = [...catalog.querySelectorAll('[data-lcg-universe-panel]')];
    const groupButtons = [...catalog.querySelectorAll('[data-lcg-group]')];
    const groupPanels = [...catalog.querySelectorAll('[data-lcg-group-panel]')];
    const topTriggers = [...root.querySelectorAll('[data-lcg-open-universe]')];
    const columns = [...catalog.querySelectorAll('.lcg-catalog__column')];
    let lastExternalTrigger = null;

    const updateMobileInert = () => {
      if (!mobileQuery.matches) {
        columns.forEach((column) => column.removeAttribute('inert'));
        return;
      }

      const activeClass = {
        universes: 'lcg-catalog__universes',
        groups: 'lcg-catalog__groups',
        details: 'lcg-catalog__details'
      }[catalog.dataset.step] || 'lcg-catalog__universes';

      columns.forEach((column) => {
        if (column.classList.contains(activeClass)) column.removeAttribute('inert');
        else column.setAttribute('inert', '');
      });
    };

    const setStep = (step, focusTarget = null) => {
      catalog.dataset.step = step;
      updateMobileInert();
      if (mobileQuery.matches) {
        const activeColumn = {
          universes: catalog.querySelector('.lcg-catalog__universes'),
          groups: catalog.querySelector('.lcg-catalog__groups'),
          details: catalog.querySelector('.lcg-catalog__details')
        }[step];
        if (activeColumn) activeColumn.scrollTop = 0;
      }
      if (focusTarget && mobileQuery.matches) {
        window.setTimeout(() => focusTarget.focus({ preventScroll: true }), 260);
      }
    };

    const updateTopTriggers = () => {
      topTriggers.forEach((button) => {
        const isActive = menu.open && button.dataset.lcgOpenUniverse === catalog.dataset.activeUniverse;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });
    };

    const selectGroup = (groupKey, { advance = false } = {}) => {
      const targetButton = groupButtons.find((button) => button.dataset.lcgGroup === groupKey);
      const targetPanel = groupPanels.find((panel) => panel.dataset.lcgGroupPanel === groupKey);
      if (!targetButton || !targetPanel) return;

      groupButtons.forEach((button) => {
        button.setAttribute('aria-expanded', button === targetButton ? 'true' : 'false');
      });
      groupPanels.forEach((panel) => {
        panel.hidden = panel !== targetPanel;
      });

      catalog.dataset.activeGroup = groupKey;
      if (advance) {
        const focusTarget = targetPanel.querySelector('.lcg-catalog__back, a, button');
        setStep('details', focusTarget);
      }
    };

    const selectUniverse = (universeKey, { advance = false } = {}) => {
      const targetButton = universeButtons.find((button) => button.dataset.lcgUniverse === universeKey);
      const targetPanel = universePanels.find((panel) => panel.dataset.lcgUniversePanel === universeKey);
      if (!targetButton || !targetPanel) return;

      universeButtons.forEach((button) => {
        button.setAttribute('aria-expanded', button === targetButton ? 'true' : 'false');
      });
      universePanels.forEach((panel) => {
        panel.hidden = panel !== targetPanel;
      });

      catalog.dataset.activeUniverse = universeKey;
      const defaultGroup = targetPanel.dataset.defaultGroup;
      if (defaultGroup) selectGroup(defaultGroup);
      updateTopTriggers();

      if (advance) {
        const focusTarget = targetPanel.querySelector('.lcg-catalog__back, a, button');
        setStep('groups', focusTarget);
      }
    };

    const closeCatalog = ({ restoreFocus = true } = {}) => {
      menu.removeAttribute('open');
      menuSummary.setAttribute('aria-expanded', 'false');
      menuSummary.setAttribute('aria-label', 'Ouvrir le menu');
      setStep('universes');
      updateTopTriggers();
      if (restoreFocus) (lastExternalTrigger || menuSummary).focus({ preventScroll: true });
      lastExternalTrigger = null;
    };

    universeButtons.forEach((button) => {
      button.addEventListener('click', () => selectUniverse(button.dataset.lcgUniverse, { advance: true }));
    });

    groupButtons.forEach((button) => {
      button.addEventListener('click', () => selectGroup(button.dataset.lcgGroup, { advance: true }));
    });

    catalog.querySelectorAll('[data-lcg-back]').forEach((button) => {
      button.addEventListener('click', () => {
        const destination = button.dataset.lcgBack;
        if (destination === 'universes') {
          const activeUniverseButton = universeButtons.find(
            (item) => item.dataset.lcgUniverse === catalog.dataset.activeUniverse
          );
          setStep('universes', activeUniverseButton);
          return;
        }

        const activeUniversePanel = universePanels.find(
          (panel) => panel.dataset.lcgUniversePanel === catalog.dataset.activeUniverse
        );
        const activeGroupButton = activeUniversePanel?.querySelector(
          `[data-lcg-group="${catalog.dataset.activeGroup}"]`
        );
        setStep('groups', activeGroupButton);
      });
    });

    catalog.querySelector('[data-lcg-catalog-close]')?.addEventListener('click', () => closeCatalog());

    topTriggers.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        lastExternalTrigger = button;
        catalog.dataset.openedFrom = 'subnav';
        selectUniverse(button.dataset.lcgOpenUniverse, { advance: mobileQuery.matches });
        menu.setAttribute('open', '');
        updateTopTriggers();
      });

      button.addEventListener('pointerenter', () => {
        if (!menu.open || mobileQuery.matches) return;
        selectUniverse(button.dataset.lcgOpenUniverse);
      });

      button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        button.click();
      });
    });

    menu.addEventListener('toggle', () => {
      if (menu.open) {
        if (catalog.dataset.openedFrom !== 'subnav') setStep('universes');
        delete catalog.dataset.openedFrom;
      } else {
        setStep('universes');
      }
      updateTopTriggers();
    });

    mobileQuery.addEventListener('change', (event) => {
      if (!event.matches && menu.open) closeCatalog({ restoreFocus: false });
      setStep(mobileQuery.matches ? 'universes' : 'groups');
      updateMobileInert();
    });

    selectUniverse('scooter');
    setStep(mobileQuery.matches ? 'universes' : 'groups');
  };

  const initDesktopMenu = (root) => {
    if (!root || root.dataset.desktopMenuReady === 'true') return;

    const controller = root.querySelector('[data-lcg-desktop-menu]');
    const popover = controller?.querySelector('[data-lcg-desktop-popover]');
    const categoryRoutes = {
      scooter: 'trottinettes-freestyles-completes', skate: 'skateboards-complets',
      roller: 'rollers-en-ligne', hockey: 'patins-hockey', bmx: 'bmx',
      protection: 'protections', lifestyle: 'vetements'
    };
    // One native link per category: hover/focus opens its menu, click visits it.
    controller?.querySelectorAll('button[data-lcg-desktop-trigger]').forEach((button) => {
      const handle = categoryRoutes[button.dataset.lcgDesktopTrigger];
      if (!handle) return;
      const link = document.createElement('a');
      [...button.attributes].forEach(({ name, value }) => {
        if (name !== 'type') link.setAttribute(name, value);
      });
      link.href = `${window.Shopify?.routes?.root || '/'}collections/${handle}`;
      link.textContent = button.textContent.replace(/[⌄▾▼]/g, '').trim();
      button.replaceWith(link);
    });
    const triggers = [...(controller?.querySelectorAll('[data-lcg-desktop-trigger]') || [])];
    const panels = [...(popover?.querySelectorAll('[data-lcg-desktop-panel]') || [])];
    if (!controller || !popover || !triggers.length || !panels.length) return;

    root.dataset.desktopMenuReady = 'true';
    let activeKey = '';
    let closeTimer = 0;
    let lastTrigger = null;
    let suppressFocusOpen = false;

    const cancelClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
    };

    const close = ({ restoreFocus = false } = {}) => {
      cancelClose();
      popover.hidden = true;
      panels.forEach((panel) => {
        panel.hidden = true;
      });
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.classList.remove('is-active');
      });
      activeKey = '';
      if (restoreFocus && lastTrigger) {
        suppressFocusOpen = true;
        lastTrigger.focus({ preventScroll: true });
        window.setTimeout(() => {
          suppressFocusOpen = false;
        }, 0);
      }
    };

    const scheduleClose = () => {
      cancelClose();
      closeTimer = window.setTimeout(() => close(), 160);
    };

    const open = (key, trigger) => {
      if (!desktopQuery.matches) return;
      const panel = panels.find((item) => item.dataset.lcgDesktopPanel === key);
      if (!panel) return;

      cancelClose();
      const universeChanged = activeKey !== key;
      lastTrigger = trigger;
      activeKey = key;
      panels.forEach((item) => {
        item.hidden = item !== panel;
      });
      triggers.forEach((item) => {
        const isActive = item === trigger;
        item.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        item.classList.toggle('is-active', isActive);
      });
      popover.hidden = false;
      if (universeChanged) popover.scrollTop = 0;
    };

    triggers.forEach((trigger, index) => {
      const key = trigger.dataset.lcgDesktopTrigger;
      trigger.addEventListener('pointerenter', () => open(key, trigger));
      trigger.addEventListener('focusin', () => {
        if (!suppressFocusOpen) open(key, trigger);
      });
      trigger.addEventListener('click', () => open(key, trigger));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          open(key, trigger);
          window.requestAnimationFrame(() => {
            panels
              .find((item) => item.dataset.lcgDesktopPanel === key)
              ?.querySelector('a[href]')
              ?.focus({ preventScroll: true });
          });
          return;
        }

        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + triggers.length) % triggers.length;
        triggers[nextIndex].focus({ preventScroll: true });
      });
    });

    controller.addEventListener('pointerenter', cancelClose);
    controller.addEventListener('pointerleave', scheduleClose);
    controller.addEventListener('focusout', (event) => {
      if (event.relatedTarget && controller.contains(event.relatedTarget)) return;
      scheduleClose();
    });

    popover.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', () => close());
    });

    document.addEventListener('pointerdown', (event) => {
      if (!activeKey || controller.contains(event.target)) return;
      close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !activeKey) return;
      event.preventDefault();
      close({ restoreFocus: true });
    });

    desktopQuery.addEventListener('change', () => close());
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('.lcg-header')) {
      initCatalog(scope);
      initDesktopMenu(scope);
    }
    scope.querySelectorAll?.('.lcg-header').forEach((root) => {
      initCatalog(root);
      initDesktopMenu(root);
    });
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
